import {
  Component,
  Inject,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FlagService } from '../../../services/flag.service';

interface FeedItem {
  id: number;
  type: 'article' | 'status' | 'comment' | 'topic' | 'discussion';
  flagged_by_current_user?: boolean;
  current_user_permissions?: {
    flag?: boolean;
  };
}

@Component({
  selector: 'ut-community-feed-item-flag',
  templateUrl: './community-feed-item-flag.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityFeedItemFlagComponent {
  @Input() item!: FeedItem;

  private isProcessing$ = new BehaviorSubject<boolean>(false);

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject('$uibModal') private $uibModal: any,
    private flagService: FlagService,
    @Inject('AlertService') private AlertService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('$analytics') private $analytics: any,
  ) {}

  get isFlagged(): boolean {
    return this.item?.flagged_by_current_user === true;
  }

  get ariaLabel(): string {
    return 'Report abuse';
  }

  get hasFlagPermission(): boolean {
    return this.item?.current_user_permissions?.flag === true;
  }

  flagItem(): void {
    if (this.LightTesterErrorService.show()) {
      return;
    }

    if (this.isFlagged) {
      return;
    }

    const modalInstance = this.$uibModal.open({
      animation: true,
      template: this.getFlagModalTemplate(),
      controller: 'ModalCtrl',
      windowClass: 'community-feed-status-flag-modal',
      resolve: {
        data: () => ({
          item: this.item,
          submitFlag: (reason: string) => this.submitFlag(reason),
          closeOverlay: () => modalInstance.close(),
        }),
      },
    });

    modalInstance.result.catch((resp: string) => {
      if (['cancel', 'backdrop click', 'escape key press'].indexOf(resp) === -1) {
        throw new Error(resp);
      }
    });
  }

  private submitFlag(reason: string): void {
    if (!reason) {
      this.AlertService.addDanger('Please fill out the reason');
      return;
    }

    if (this.isProcessing$.value) {
      return;
    }

    this.isProcessing$.next(true);

    this.flagService.flag(this.item.type, this.item.id, reason)
      .then(() => {
        this.item.flagged_by_current_user = true;
        this.trackFlagging({ type: this.item.type });
        this.AlertService.add('Your report has been submitted');
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.AlertService.addDanger('There was an error submitting your report');
        this.cdr.markForCheck();
      })
      .finally(() => {
        this.isProcessing$.next(false);
        this.cdr.markForCheck();
      });
  }

  private trackFlagging(options: { type: string }): void {
    this.$analytics.eventTrack('Flagged', {
      category: 'Flag',
      label: options.type,
    });
  }

  private getFlagModalTemplate(): string {
    return `
      <div class="modal-header">
        <button type="button" class="close" ng-click="cancel()" aria-label="Close">
          <i class="icon-cancel"></i>
        </button>
        <h2 class="modal-title">Report Abuse</h2>
      </div>
      <div class="modal-body">
        <label for="flagReason" class="feed-item-flag-message">
          Why are you reporting this post?
        </label>
        <textarea id="flagReason" name="flag-reason" ng-model="flagReason" autofocus></textarea>
        <button class="community-cta__button unfilled" ng-click="cancel()">Cancel</button>
        <button class="community-cta__button" ng-click="submitFlag(flagReason)" ng-mouseup="cancel()">Submit</button>
      </div>
    `;
  }
}
